import { useMemo } from 'react';
import { getProductPromotionType } from '~/features/basket/utils/getProductPromotionType';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { Product, PromotionType } from '~/lib/data-contract/salesforce';
import useProductPersonalization from '~/services/personalization/useProductPersonalization';
import { useAppliedProductSpecialPrice } from '~/services/special-price';
import { getIsPromotionDiscountHeldBack } from '~/shared/utils/get-is-promotion-discount-held-back';
import { useTranslation } from '~/shared/utils/translation';
import {
    StyledProductFinalPrice,
    StyledProductFinalPriceLabel,
    StyledProductFinalPriceValue,
    StyledProductPrice,
} from '../styled';

type Props = {
    lineItem: Product;
    membership: boolean;
};

export const ProductFinalPrice = ({ lineItem, membership }: Props) => {
    const { translate } = useTranslation();
    const { isPlusMember } = useCustomer();
    const { addPersonalizationCostToPrice } = useProductPersonalization(lineItem);
    const productPromotionType = getProductPromotionType(lineItem);
    const isPromotionDiscountHeldBack = getIsPromotionDiscountHeldBack(lineItem);
    const {
        isActive: hasSpecialPrice,
        hasMetRequirements,
        dynamicSplashText: specialPriceSplash,
    } = useAppliedProductSpecialPrice(lineItem);

    const splashText = lineItem?.c_splashText;
    const isMember = membership || !!isPlusMember;

    // Promotions that will be treated with regular prices.
    const disregardedPromotions = [PromotionType.IMERCO_PRICE];

    const hasOngoingPromotion =
        !isPromotionDiscountHeldBack &&
        ((!!productPromotionType && !disregardedPromotions.includes(productPromotionType)) ||
            hasSpecialPrice);

    const isPromotionApplied = useMemo(() => {
        if (isPromotionDiscountHeldBack) return false;

        if (
            productPromotionType === PromotionType.BASTARD ||
            productPromotionType === PromotionType.BUNDLE
        )
            return lineItem.c_promotionType?.isInPromotion;

        // Special Price is only applied when user has met campaign requirements.
        if (productPromotionType === PromotionType.SPECIAL_PRICE || hasSpecialPrice) {
            return hasMetRequirements && lineItem.c_promotionType?.isInPromotion;
        }

        // Member Price is only applied when user is a member.
        if (productPromotionType === PromotionType.MEMBER_PRICE) return isMember;

        // Multi Buy/Mix and Match are only applied when user gets a discount on the units.
        if (
            productPromotionType === PromotionType.MULTI_BUY ||
            productPromotionType === PromotionType.MIX_AND_MATCH
        )
            return !!lineItem.c_promotionType?.unitDiscount;

        return hasOngoingPromotion;
    }, [
        isPromotionDiscountHeldBack,
        productPromotionType,
        hasMetRequirements,
        hasSpecialPrice,
        lineItem.c_promotionType?.unitDiscount,
        lineItem.c_promotionType?.isInPromotion,
    ]);

    const price = useMemo(() => {
        // What the customer pays now, not a promotional price yet to come.
        if (isPromotionDiscountHeldBack) return lineItem.priceAfterItemDiscount;

        if (productPromotionType) {
            if (productPromotionType === PromotionType.SPECIAL_PRICE)
                return lineItem.priceAfterOrderDiscount;

            if (productPromotionType === PromotionType.MEMBER_PRICE) {
                const unitPrice = lineItem?.c_promotionType?.unitPromotionPrice ?? 0;
                const quantity = lineItem?.quantity ?? 1;
                // UI-only price for crawlers
                const calculatedPrice = unitPrice * quantity;

                return calculatedPrice > 0 ? calculatedPrice : lineItem.priceAfterItemDiscount;
            }

            /**
             * This will cover unhandled and known promotions (i.e., DISCOUNT_PRICE, CHRISTMAS_PRICE,
             * SUPER_PRICE, IMERCO_PRICE, MIX_AND_MATCH).
             */
            return lineItem.priceAfterItemDiscount;
        } else {
            /**
             * There are cases where Special Pricing campaings have null c_promotionType.type. We will
             * fallback to our own promotion type in this case.
             */
            if (hasSpecialPrice) {
                return lineItem.priceAfterOrderDiscount;
            }

            return lineItem.price;
        }
    }, [
        isPromotionDiscountHeldBack,
        productPromotionType,
        hasSpecialPrice,
        lineItem.price,
        lineItem.priceAfterItemDiscount,
        lineItem.priceAfterOrderDiscount,
    ]);

    const label = useMemo(() => {
        if (hasSpecialPrice) return specialPriceSplash;

        if (hasOngoingPromotion && splashText) return splashText;

        return translate('basket.price');
    }, [specialPriceSplash, splashText, hasOngoingPromotion]);

    const shouldUseBasePersonalizationCost =
        productPromotionType === PromotionType.MEMBER_PRICE && !isMember;

    return (
        <StyledProductPrice>
            <StyledProductFinalPrice>
                <StyledProductFinalPriceLabel isHighlighted={hasOngoingPromotion}>
                    {label}
                </StyledProductFinalPriceLabel>

                <StyledProductFinalPriceValue
                    aria-label={`Samlet pris for ${lineItem?.productName} er ${addPersonalizationCostToPrice(price, shouldUseBasePersonalizationCost)} kroner.`}
                    isHighlighted={isPromotionApplied}
                >
                    {addPersonalizationCostToPrice(price, shouldUseBasePersonalizationCost)}
                </StyledProductFinalPriceValue>
            </StyledProductFinalPrice>
        </StyledProductPrice>
    );
};
